/**
 * The studio's direct-edit strip (spec 025 D-4): the small fixes Maya makes
 * most after a draft, on the selected scene and nothing else.
 *
 * - Lines: each dialog line's text, committed with Enter or on blur (one
 *   `updateDialog`), and its expression (spec 026): the speaking character
 *   and how they look while the line plays (one `setLineExpression`).
 * - Pictures (spec 026): each character's expressions, filled or still
 *   needed, and its picture from the media library (images only). Every
 *   expression is a slot of the story's picture map (D-18), so the choice
 *   fills it everywhere it is used (`storyStore.fillPicture`, one story
 *   history entry, D-20); only a sprite whose name has no letter or digit has
 *   no slot and is assigned in this scene (`assignStateMedia`, one scene undo
 *   step).
 * - Choices: their order, with up/down buttons and drag handles.
 *
 * Lines and Pictures share one place as two tabs (Lines first, each with its
 * count); only the active tab's panel shows, and "See all pictures" sits at
 * the tab row's end while Pictures is active. Choices stay below the tabs.
 * The strip fills the height the result area leaves under the preview: the
 * tab row stays put and the panel, Choices and the footer scroll inside it.
 *
 * Under a scene picture owner (spec 029 D-9, spec 030: the scene studio of a
 * standalone scene) the Pictures part reads the scene's own picture map
 * (`owner.map`) instead of the story's: a picture choice fills the scene's
 * slot (`sceneStore.fillPicture`, every sprite of that name and expression in
 * the scene, one scene undo step, saved at once), the Pictures link and the
 * extension's Generate open the scene's Pictures view, and nothing reads or
 * writes `storyStore`. Without one, the story's map, as 025 and 026 say.
 *
 * Structure (scenes, connections, choice text and count, timing, layout,
 * sounds, backgrounds) goes through the chat or under the hood.
 */

import React, { useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChatQuote, Images } from 'react-bootstrap-icons';
import type { Dialog, DialogLine, PictureMap, PictureSlot, Sprite, Story } from '../../../../Shared/src/@types';
import { dialogsInPlayOrder, isSlotNeeded, scenePictureSlots } from '../../../../Shared/src/pictures';
import { useSceneStore } from '../../stores/sceneStore';
import { useStoryStore } from '../../stores/storyStore';
import { loadNaturalSize } from '../../stores/assistantStore';
import { useMediaSheetStore } from '../../stores/mediaSheetStore';
import { useModalContext } from '../../contexts/ModalContext';
import MediaPicker from '../../modals/MediaPicker';
import { usePictureExtensions } from '../pictures/pictureExtensions';
import { PictureOwnerContext } from '../pictures/pictureOwner';
import PictureThumb from '../pictures/PictureThumb';
import { useOpenPictures } from '../pictures/usePicturesNav';
import styles from './StoryStudio.module.scss';
import pictureStyles from '../pictures/Pictures.module.scss';

// The play-order rule moved to Shared with the picture map (spec 026).
export { dialogsInPlayOrder };

export const DIRECT_EDITS_TEXT = {
  footer: 'Placeholders show their label in Preview. To change more, ask the assistant or',
  noExpression: 'No expression',
  whoIsSpeaking: 'Who is speaking?',
  expressionTitle: 'Expression while speaking',
  backToDefault: 'Back to default after the line',
  expressionNote: 'Picking an expression that has no picture yet still saves it. The line plays the default picture and the slot shows up in Pictures.',
} as const;

export interface DirectEditsProps {
  /** The selected scene as the scene store holds it. */
  scene: Story;
  /** The scene's key in the story's picture map (`ss-<storySceneId>`, spec 026). */
  sceneKey?: string;
  disabled?: boolean;
  /** The scene is a reference shared with other places (010). */
  shared?: boolean;
  srcFor: (mediaId: number) => string | undefined;
  onOpenHood: () => void;
}


const LineField: React.FC<{ dialog: Dialog; lineId: string; text: string; disabled?: boolean; label: string }> = ({
  dialog, lineId, text, disabled, label,
}) => {
  const [value, setValue] = useState(text);
  useEffect(() => { setValue(text); }, [text]);
  const commit = () => {
    if (value === text) return;
    const live = useSceneStore.getState().scene?.dialogs.find((d) => d.id === dialog.id) ?? dialog;
    useSceneStore.getState().updateDialog(dialog.id, {
      lines: live.lines.map((l) => (l.id === lineId ? { ...l, text: value } : l)),
    });
  };
  return (
    <input
      type="text"
      className={styles.lineInput}
      value={value}
      disabled={disabled}
      aria-label={label}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          setValue(text);
        }
      }}
      data-testid={`studio-line-${lineId}`}
    />
  );
};

const stateOf = (sprite: Sprite | undefined, name: string | undefined) => sprite?.states.find((s) => s.name === name);
const hasPicture = (mediaId: number | undefined) => typeof mediaId === 'number' && mediaId > 0;
const defaultPicture = (sprite: Sprite) => stateOf(sprite, sprite.defaultState)?.mediaId ?? sprite.states[0]?.mediaId ?? 0;

const NO_PICTURE_MAP: PictureMap = { slots: [] };

export type DirectEditsTab = 'lines' | 'pictures';
const TABS: { id: DirectEditsTab; label: string; Icon: typeof ChatQuote }[] = [
  { id: 'lines', label: 'Lines', Icon: ChatQuote },
  { id: 'pictures', label: 'Pictures', Icon: Images },
];

/**
 * The scene's slice of the story's picture map (`scenePictureSlots`): the
 * slots used in this scene as the map will read once the live scene is
 * saved, so unsaved edits count and the count agrees with the Pictures view
 * (a slot filled here but showing no picture in another scene is needed).
 */
function useScenePictureMap(scene: Story, sceneKey: string, enabled: boolean): PictureMap {
  const map = useStoryStore((s) => (enabled ? s.pictureMap : null));
  return useMemo(
    () => (map ? { slots: scenePictureSlots(map, { key: sceneKey, name: scene.name, doc: scene as never }) } : NO_PICTURE_MAP),
    [map, scene, sceneKey],
  );
}

const slotOfState = (map: PictureMap, spriteId: string, state: string): PictureSlot | undefined =>
  map.slots.find((s) => s.uses.some((u) => u.kind === 'sprite' && u.spriteId === spriteId && u.state === state));

/** One line's header and its expression editor (spec 026). */
const LineExpressionEditor: React.FC<{
  scene: Story;
  dialog: Dialog;
  line: DialogLine;
  lineNo: number;
  disabled?: boolean;
  srcFor: (mediaId: number) => string | undefined;
}> = ({ scene, dialog, line, lineNo, disabled, srcFor }) => {
  const setLineExpression = useSceneStore((s) => s.setLineExpression);
  const [open, setOpen] = useState(false);
  const expression = line.expression;
  const speakerOf = (id: string | undefined) => scene.sprites.find((sp) => sp.id === id);
  const current = speakerOf(expression?.spriteId);
  const [chosen, setChosen] = useState<string | null>(null);
  const speaker = current ?? speakerOf(chosen ?? undefined);
  const [back, setBack] = useState(expression?.backToDefault ?? true);
  useEffect(() => { if (expression) setBack(expression.backToDefault); }, [expression]);

  const exprState = current ? stateOf(current, expression?.state) : undefined;
  const headPicture = current ? (hasPicture(exprState?.mediaId) ? exprState!.mediaId : defaultPicture(current)) : 0;
  const needed = !!current && !!expression && !hasPicture(exprState?.mediaId);

  const pick = (state: string | null) => {
    if (state === null) {
      setLineExpression(dialog.id, line.id, undefined);
      return;
    }
    if (!speaker) return;
    setLineExpression(dialog.id, line.id, { spriteId: speaker.id, state, backToDefault: back });
  };
  const toggleBack = () => {
    const next = !back;
    setBack(next);
    if (expression && current) setLineExpression(dialog.id, line.id, { ...expression, backToDefault: next });
  };

  return (
    <>
      <button
        type="button"
        className={pictureStyles.lineHead}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={`studio-line-head-${line.id}`}
      >
        <PictureThumb className={pictureStyles.lineThumb} filled={hasPicture(headPicture)} src={headPicture ? srcFor(headPicture) : undefined} />
        {current && expression ? (
          <>
            <span>{current.name}</span>
            <span data-testid={`studio-line-expression-${line.id}`}>line {lineNo} · {expression.state}</span>
            {needed && <span className={pictureStyles.needed} data-testid={`studio-line-needed-${line.id}`}>NEEDED</span>}
          </>
        ) : (
          <span data-testid={`studio-line-expression-${line.id}`}>line {lineNo} · {DIRECT_EDITS_TEXT.noExpression}</span>
        )}
      </button>
      {open && (
        <div className={pictureStyles.lineEditor} data-testid={`studio-line-editor-${line.id}`}>
          <p><strong>Line {lineNo}{speaker ? ` · ${speaker.name}` : ''}</strong></p>
          <p className={pictureStyles.lineQuote}>“{line.text ?? ''}”</p>
          {!speaker && (
            <>
              <p>{DIRECT_EDITS_TEXT.whoIsSpeaking}</p>
              <div className={pictureStyles.chips}>
                {scene.sprites.map((sp) => (
                  <button
                    key={sp.id}
                    type="button"
                    className={pictureStyles.chip}
                    disabled={disabled}
                    onClick={() => setChosen(sp.id)}
                    data-testid={`studio-speaker-${sp.id}`}
                  >
                    {sp.name}
                  </button>
                ))}
              </div>
            </>
          )}
          {speaker && (
            <>
              <p>{DIRECT_EDITS_TEXT.expressionTitle}</p>
              <div className={pictureStyles.tiles}>
                {speaker.states.map((st) => {
                  const on = !!expression && expression.spriteId === speaker.id && expression.state === st.name;
                  return (
                    <button
                      key={st.name}
                      type="button"
                      className={`${pictureStyles.tile} ${on ? pictureStyles.tileOn : ''}`}
                      aria-pressed={on}
                      disabled={disabled}
                      onClick={() => pick(st.name)}
                      data-testid={`studio-expression-tile-${line.id}-${st.name}`}
                    >
                      <PictureThumb className={pictureStyles.tileThumb} filled={hasPicture(st.mediaId)} src={hasPicture(st.mediaId) ? srcFor(st.mediaId) : undefined} />
                      <span>{st.name}</span>
                      {!hasPicture(st.mediaId) && <span className={pictureStyles.needed}>NEEDED</span>}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className={`${pictureStyles.tile} ${!expression ? pictureStyles.tileOn : ''}`}
                  aria-pressed={!expression}
                  disabled={disabled}
                  onClick={() => pick(null)}
                  data-testid={`studio-expression-tile-${line.id}-none`}
                >
                  <span>{DIRECT_EDITS_TEXT.noExpression}</span>
                </button>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={back}
                className={pictureStyles.switch}
                disabled={disabled}
                onClick={toggleBack}
                data-testid={`studio-back-to-default-${line.id}`}
              >
                <span className={`${pictureStyles.switchTrack} ${back ? pictureStyles.switchOn : ''}`} aria-hidden="true" />
                {DIRECT_EDITS_TEXT.backToDefault}
              </button>
            </>
          )}
          <p className={pictureStyles.hint}>{DIRECT_EDITS_TEXT.expressionNote}</p>
        </div>
      )}
    </>
  );
};

export const DirectEdits: React.FC<DirectEditsProps> = ({ scene, sceneKey, disabled, shared, srcFor, onOpenHood }) => {
  const { addModal, removeModal } = useModalContext();
  const assignStateMedia = useSceneStore((s) => s.assignStateMedia);
  const moveChoice = useSceneStore((s) => s.moveChoice);
  const ext = usePictureExtensions();
  const openPictures = useOpenPictures();
  const dialogs = useMemo(() => dialogsInPlayOrder(scene.dialogs ?? []), [scene.dialogs]);
  const choices = scene.choices ?? [];
  const [dragId, setDragId] = useState<string | null>(null);
  const [tab, setTab] = useState<DirectEditsTab>('lines');
  const tabRefs = useRef<Record<DirectEditsTab, HTMLButtonElement | null>>({ lines: null, pictures: null });
  const idBase = useId();
  const lineCount = dialogs.reduce((n, d) => n + d.lines.length, 0);
  // A standalone scene's own map (spec 029 D-9), else the story's slice for this scene.
  const provided = useContext(PictureOwnerContext);
  const sceneOwner = provided?.noun === 'scene' ? provided : null;
  const storyMap = useScenePictureMap(scene, sceneKey ?? `scene-${scene.id}`, !sceneOwner);
  const map = sceneOwner ? sceneOwner.map : storyMap;
  const neededHere = map.slots.filter((s) => isSlotNeeded(s)).length;
  // The selected expression per character (the default first).
  const [picked, setPicked] = useState<Record<string, string>>({});
  const selectedState = (sp: Sprite) => {
    const p = picked[sp.id];
    return sp.states.some((s) => s.name === p) ? p : stateOf(sp, sp.defaultState)?.name ?? sp.states[0]?.name;
  };

  const changePicture = (sp: Sprite, stateName: string) => {
    addModal({
      title: `Choose a picture for ${sp.name}`,
      size: 'lg',
      body: (
        <MediaPicker
          typeFilter="image"
          lockFilter
          onSelect={async (mediaId: number) => {
            removeModal();
            const slot = slotOfState(map, sp.id, stateName);
            if (slot) {
              if (sceneOwner) await sceneOwner.fillPicture(slot.id, mediaId);
              else await useStoryStore.getState().fillPicture(slot.id, mediaId);
              return;
            }
            // The natural size; assignStateMedia sizes a spritesheet by its frame (spec 033).
            const [natural] = await Promise.all([loadNaturalSize(mediaId), useMediaSheetStore.getState().loadSheets([mediaId])]);
            assignStateMedia(sp.id, stateName, mediaId, natural);
          }}
          onCancel={() => removeModal()}
        />
      ),
    });
  };

  // Arrow keys, Home and End move between the tabs and select (WAI-ARIA tabs).
  const onTabKey = (e: React.KeyboardEvent) => {
    const i = TABS.findIndex((t) => t.id === tab);
    const next = e.key === 'ArrowRight' ? (i + 1) % TABS.length
      : e.key === 'ArrowLeft' ? (i - 1 + TABS.length) % TABS.length
        : e.key === 'Home' ? 0
          : e.key === 'End' ? TABS.length - 1 : -1;
    if (next < 0) return;
    e.preventDefault();
    setTab(TABS[next].id);
    tabRefs.current[TABS[next].id]?.focus();
  };
  const countOf = (id: DirectEditsTab) => (id === 'lines' ? lineCount : scene.sprites.length);

  let lineNo = 0;
  return (
    <div className={styles.strip} data-testid="studio-edits">
      {shared && (
        <p className={styles.sharedNote} data-testid="studio-shared-note">
          This scene is also used in other stories; changes show there too.
        </p>
      )}

      <div className={styles.tabRow}>
        <div className={styles.tabList} role="tablist" aria-label="Direct edits" onKeyDown={onTabKey}>
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              ref={(el) => { tabRefs.current[id] = el; }}
              type="button"
              role="tab"
              id={`${idBase}-tab-${id}`}
              aria-selected={tab === id}
              aria-controls={`${idBase}-panel-${id}`}
              tabIndex={tab === id ? 0 : -1}
              className={`${styles.tab} ${tab === id ? styles.tabOn : ''}`}
              onClick={() => setTab(id)}
              data-testid={`studio-tab-${id}`}
            >
              <Icon className={styles.tabIcon} aria-hidden="true" />
              {label}
              <span className={styles.tabCount}>{countOf(id)}</span>
            </button>
          ))}
        </div>
        {tab === 'pictures' && (
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => openPictures(neededHere > 0 ? { filter: 'needed' } : undefined)}
            data-testid="studio-pictures-link"
          >
            {neededHere > 0 ? `${neededHere} needed in this scene · see all pictures` : 'See all pictures'}
          </button>
        )}
      </div>

      {/* Pane-scoped scrolling: the tab row stays put; the active panel, the
          choices and the footer scroll here, in the height the result leaves. */}
      <div className={styles.stripScroll} data-testid="studio-edits-scroll">
        <section
          className={styles.stripSection}
          role="tabpanel"
          id={`${idBase}-panel-lines`}
          aria-labelledby={`${idBase}-tab-lines`}
          hidden={tab !== 'lines'}
          data-testid="studio-panel-lines"
        >
          {dialogs.every((d) => d.lines.length === 0) && <p className={styles.stripEmpty}>No lines in this scene.</p>}
          {dialogs.map((d) => d.lines.map((l) => {
            lineNo += 1;
            return (
              <div key={`${d.id}:${l.id}`} data-testid={`studio-line-row-${l.id}`}>
                <LineExpressionEditor scene={scene} dialog={d} line={l} lineNo={lineNo} disabled={disabled} srcFor={srcFor} />
                <LineField dialog={d} lineId={l.id} text={l.text ?? ''} disabled={disabled} label={`Line ${lineNo}`} />
              </div>
            );
          }))}
        </section>

        <section
          className={styles.stripSection}
          role="tabpanel"
          id={`${idBase}-panel-pictures`}
          aria-labelledby={`${idBase}-tab-pictures`}
          hidden={tab !== 'pictures'}
          data-testid="studio-panel-pictures"
        >
          {scene.sprites.length === 0 && <p className={styles.stripEmpty}>No characters in this scene.</p>}
          {scene.sprites.map((sp) => {
            const def = defaultPicture(sp);
            const anyPicture = sp.states.some((st) => hasPicture(st.mediaId));
            const sel = selectedState(sp);
            const selState = stateOf(sp, sel);
            const slot = sel ? slotOfState(map, sp.id, sel) : undefined;
            return (
              <div key={sp.id} className={pictureStyles.character} data-testid={`studio-character-${sp.id}`}>
                <div className={pictureStyles.characterTop}>
                  <PictureThumb className={pictureStyles.smallThumb} filled={hasPicture(def)} src={hasPicture(def) ? srcFor(def) : undefined} />
                  <span className={pictureStyles.characterName} data-testid={`studio-character-name-${sp.id}`}>
                    {sp.name}{anyPicture ? '' : ' · placeholder'}
                  </span>
                </div>
                {sp.states.length > 0 && (
                  <div className={pictureStyles.chips}>
                    {sp.states.map((st) => {
                      const filled = hasPicture(st.mediaId);
                      return (
                        <button
                          key={st.name}
                          type="button"
                          className={`${pictureStyles.chip} ${filled ? pictureStyles.chipFilled : pictureStyles.chipNeeded} ${sel === st.name ? pictureStyles.chipOn : ''}`}
                          aria-pressed={sel === st.name}
                          disabled={disabled}
                          onClick={() => setPicked((p) => ({ ...p, [sp.id]: st.name }))}
                          data-testid={`studio-expression-${sp.id}-${st.name}`}
                          data-state={filled ? 'filled' : 'needed'}
                        >
                          {filled ? `✓ ${st.name}` : st.name}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className={pictureStyles.characterTop}>
                  {ext.stripAction?.({ id: sp.id, name: sp.name }, slot)}
                  <button
                    type="button"
                    className={styles.stripButton}
                    disabled={disabled || !selState}
                    onClick={() => selState && changePicture(sp, selState.name)}
                    data-testid={`studio-change-picture-${sp.id}`}
                  >
                    {selState && hasPicture(selState.mediaId) ? 'Change picture' : 'Pick picture'}
                  </button>
                </div>
              </div>
            );
          })}
        </section>

        {choices.length > 0 && (
          <section className={styles.stripSection} aria-label="Choices">
            <h4 className={styles.stripTitle}>Choices</h4>
            <ol className={styles.choiceList}>
              {choices.map((c, i) => (
                <li
                  key={c.id}
                  className={`${styles.choiceRow} ${dragId === c.id ? styles.choiceDragging : ''}`}
                  draggable={!disabled}
                  onDragStart={(e) => { setDragId(c.id); e.dataTransfer?.setData('text/plain', c.id); }}
                  onDragEnd={() => setDragId(null)}
                  onDragOver={(e) => { if (dragId) e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragId && dragId !== c.id) moveChoice(dragId, i);
                    setDragId(null);
                  }}
                  data-testid={`studio-choice-${c.id}`}
                >
                  <span className={styles.dragHandle} aria-hidden="true">⠿</span>
                  <span className={styles.choiceText}>{c.text || 'Untitled choice'}</span>
                  <button
                    type="button"
                    className={styles.stripIcon}
                    disabled={disabled || i === 0}
                    onClick={() => moveChoice(c.id, i - 1)}
                    aria-label={`Move "${c.text || 'Untitled choice'}" up`}
                    data-testid={`studio-choice-up-${c.id}`}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className={styles.stripIcon}
                    disabled={disabled || i === choices.length - 1}
                    onClick={() => moveChoice(c.id, i + 1)}
                    aria-label={`Move "${c.text || 'Untitled choice'}" down`}
                    data-testid={`studio-choice-down-${c.id}`}
                  >
                    ↓
                  </button>
                </li>
              ))}
            </ol>
          </section>
        )}

        <p className={styles.stripMore}>
          {DIRECT_EDITS_TEXT.footer}{' '}
          <button type="button" className={styles.linkButton} onClick={onOpenHood} data-testid="studio-open-hood">
            open Under the hood
          </button>
          .
        </p>
      </div>
    </div>
  );
};

export default DirectEdits;
